import { db } from '@/lib/db';
import type { GroupRoundListItem } from '@/lib/db/types';

export type PlayTabCache = {
  activeRounds: GroupRoundListItem[];
  history: GroupRoundListItem[];
  joinableRounds: GroupRoundListItem[];
  courseNames: Record<string, string>;
  fetchedAt: number;
};

const STALE_MS = 45_000;

let cache: PlayTabCache | null = null;
let inflight: Promise<PlayTabCache> | null = null;

export function getPlayTabCache(): PlayTabCache | null {
  return cache;
}

export function invalidatePlayTabCache() {
  cache = null;
}

export function removeRoundFromPlayTabCache(roundId: string) {
  if (!cache) return;
  cache = {
    ...cache,
    activeRounds: cache.activeRounds.filter((r) => r.id !== roundId),
    history: cache.history.filter((r) => r.id !== roundId),
    joinableRounds: cache.joinableRounds.filter((r) => r.id !== roundId),
    fetchedAt: Date.now(),
  };
}

export function setPlayTabCache(data: PlayTabCache) {
  cache = data;
}

export async function loadPlayTabData(
  opts: { force?: boolean; onStage?: (partial: PlayTabCache) => void } = {},
): Promise<PlayTabCache> {
  if (!opts.force && cache && Date.now() - cache.fetchedAt < STALE_MS) {
    return cache;
  }
  if (inflight) return inflight;

  inflight = (async () => {
    const [active, courses] = await Promise.all([
      db.groupRounds.listActiveSummary(),
      db.courses.list(),
    ]);
    const courseNames = Object.fromEntries(courses.map((c) => [c.id, c.name]));

    const staged: PlayTabCache = {
      activeRounds: active,
      history: cache?.history ?? [],
      joinableRounds: cache?.joinableRounds ?? [],
      courseNames,
      fetchedAt: Date.now(),
    };
    opts.onStage?.(staged);

    const [history, joinable] = await Promise.all([
      db.groupRounds.listHistorySummary(10),
      db.groupRounds.listJoinableSummary().catch((err) => {
        console.warn('[play-tab] listJoinableSummary failed:', err);
        return [] as GroupRoundListItem[];
      }),
    ]);

    cache = {
      activeRounds: active,
      history,
      joinableRounds: joinable,
      courseNames,
      fetchedAt: Date.now(),
    };
    return cache;
  })().finally(() => {
    inflight = null;
  });

  return inflight;
}
